import { flatten, unflatten } from 'flat';
import { cloneDeep, debounce, Dictionary, has, isArray, isBoolean, isEmpty, isEqual, isNumber, isString, isUndefined, join, get as lodashGet, set as lodashSet, map, omitBy, slice, toNumber, toPath, unset, values } from 'lodash';
import { action, computed, get, observable, set } from 'mobx';
import { FBFormConstructor, FBInputState, FBLockBody, FBLockValue, FBSchemaProps, FBValidationRelatedRules, FBWorkspaceMode, FBWorkspaceSkipValidationModes, FBWorkspaceState } from '..';
import { translate } from '../../../common/intl';
import { isEmptyValue } from '../../common/utils/helpers';
import FBSchemaState from '../FBSchema/FBSchema.state';
import { getConditionalRules } from './conditionalRules';

const omitFieldPath = (source: Dictionary<any>, fieldName: string): Dictionary<any> => {
  const path = toPath(fieldName);
  const result = { ...source };
  if (path.length > 1 && has(source, path)) {
    // Clone the changed branch, including arrays, before removing a nested value.
    result[path[0]] = cloneDeep(source[path[0]]);
    unset(result, path);
  }
  // Defaults may be stored under a literal dotted key as well as a nested path.
  delete result[fieldName];
  return result;
};

class FBFormState extends FBSchemaState {
  public rules: Dictionary<string> = {};
  public validationAttributeNames: Dictionary<string> = {};
  public errorValidation = true;
  public initialValues: Dictionary<any> = {};
  public workspaceMode?: FBWorkspaceMode;
  @observable public values: Dictionary<any> = {};
  public validationValues: Dictionary<any> = {};
  private finalValidationRulse: Dictionary<string> = {};
  @observable.ref public validatorRules: Record<string, FBValidationRelatedRules[]> = {};
  public validationRelatedRules: Record<string, FBValidationRelatedRules[]> = {};
  public includeLocking = false;
  public expireLocks: Record<string, string> = {};

  private readonly validationOwners = new Set<symbol>();
  private pendingMountValidation?: Set<symbol>;
  private changeValidationPending = false;
  private validationSequence = 0;
  private readonly debouncedChangeValidation = debounce(() => this.validate(), 150, { maxWait: 500 });

  // This will be used for reaction on value change
  // so that we can omit Input state
  @observable public newValues = new Map<string, any>();
  public oldValues = {};

  // Naming? Don't know, don't ask!
  @observable isBackdropOpen = false;

  // TO BBE REMOVED!
  public workspaceState?: FBWorkspaceState;
  @observable public errors = new Map<string, string>();
  @observable public helpers = new Map<string, string>();
  @observable public inputState = new Map<string, FBInputState>();
  @observable public locked = new Map<string, FBLockValue>();
  @observable public approvalMap = {};

  public constructor ({
    schema,
    initialValues = {},
    workspaceMode, workspaceState,
  }: FBFormConstructor) {
    super();
    this.schema = schema;
    this.initialValues = initialValues || {};
    this.values = initialValues || {};
    map(flatten(initialValues || {}), (value, key) => {
      this.validationValues[key] = value;
    });
    this.validationValues = unflatten(this.validationValues);
    this.workspaceMode = workspaceMode;
    this.workspaceState = workspaceState;
    this.initializeMPIValues(schema);
  }

  /** Seed defaults without treating deferred input registration as a user edit. */
  @action public initializeMPIValues = (schema: FBSchemaProps[] = []) => {
    schema.forEach(({ type, name, deleted, editorConfig }) => {
      if (type !== 'mpiprocedure' || !name || deleted) { return; }
      const fieldName = `${name}.description`;
      if (!isUndefined(lodashGet(this.values, fieldName))) { return; }
      const initialValue = lodashGet(this.initialValues, fieldName);
      const description = isUndefined(initialValue) ? editorConfig?.description : initialValue;
      if (isUndefined(description)) { return; }
      lodashSet(this.values, fieldName, description);
      this.setValidationValue(fieldName, description);
    });
  };

  @computed public get Validator (): any {
    return lodashGet(window, 'EnlilRemoteValidators.validator');
  }

  public get shouldValidate (): boolean {
    return !FBWorkspaceSkipValidationModes.some((mode) => this.workspaceMode === mode);
  }

  public setIncludeLocking = (state: boolean) => {
    this.includeLocking = state;
  };

  public reset = () => {
    this.initialValues = {};
    this.values = {};
    this.rules = {};
    this.validationAttributeNames = {};
    this.inputState.clear();
    this.validate();
  };

  public getInputState = (name: string): FBInputState | undefined =>
    this.inputState?.get(name);

  @action public setValues = (values: Dictionary<any>) => {
    this.values = values;
  };

  @action public getValues = () => this.values;

  @action public getFieldValue = (
    fieldName: string | undefined,
    defaultValue: any = undefined,
    omitFormValue = false,
  ): any => {
    if (!fieldName) {
      return defaultValue;
    }
    if (isUndefined(lodashGet(this.values, fieldName)) && !isUndefined(defaultValue)) {
      this.setValidationValue(fieldName, defaultValue);
      this.initialValues = {
        ...this.initialValues,
        [fieldName]: defaultValue,
      };
      if (!omitFormValue) {
        lodashSet(this.values, fieldName, defaultValue);
      }
    }
    // Explicit clears must survive refreshes that still carry the previous initial values.
    const wasCleared = this.newValues.has(fieldName) && isUndefined(this.newValues.get(fieldName));
    if (isUndefined(lodashGet(this.values, fieldName)) && !wasCleared) {
      return lodashGet(this.initialValues, fieldName);
    }
    return lodashGet(this.values, fieldName);
  };

  private readonly setValidationValue = (fieldName: string, value: any) => {
    if (isBoolean(value)) {
      // validatorjs doesn't validate boolean values
      value = `${value}`;
    }
    lodashSet(this.validationValues, fieldName, cloneDeep(value));
  };

  @action public setFieldValue = (
    fieldName: string | undefined,
    value: any,
    withState = false,
    omitFormValue = false,
  ) => {
    if (!fieldName) { return; }
    value = cloneDeep(value);
    if (value !== undefined) { this.workspaceState?.clearedFormInputKeys?.delete(fieldName); }
    this.newValues.set(fieldName, value);
    const fieldPath = fieldName in this.values ? [fieldName] : toPath(fieldName);
    const rootField = fieldPath[0];
    const oldValues = { ...this.values };
    // Approval reactions need the previous changed field, not deep copies of unrelated fields.
    if (fieldPath.length > 1 && has(this.values, [rootField])) {
      oldValues[rootField] = cloneDeep(this.values[rootField]);
    }
    this.oldValues = oldValues;
    if (!omitFormValue) {
      lodashSet(this.values, fieldName, value);
    }
    this.setValidationValue(fieldName, value);
    if (!withState) { return; }
    this.inputState.get(fieldName)?.setValue(value);
  };

  @action public omitFieldValue = (fieldName?: string, includeDependents = false) => {
    if (!fieldName) { return; }
    const fields = new Set([fieldName]);
    if (includeDependents) {
      // Include schema rules so deferred, unmounted and required-only descendants are cleared too.
      const rulesByField = new Map<string, unknown>(Object.entries(this.validatorRules));
      [...(this.schema ?? []), ...(this.workspaceState?.schema ?? [])].forEach(item => {
        if (item.name && !item.deleted && item.rules !== undefined) {
          rulesByField.set(item.name, item.rules);
        }
      });
      Object.entries(this.rules).forEach(([name, rules]) => rulesByField.set(name, rules));
      const dependents = new Map<string, Set<string>>();
      rulesByField.forEach((rules, name) => {
        getConditionalRules(rules).forEach(({ fieldName: parent }) => {
          const children = dependents.get(parent) ?? new Set<string>();
          children.add(name);
          dependents.set(parent, children);
        });
      });
      // A Set also prevents cycles in conditional rules from revisiting an input.
      fields.forEach(name => dependents.get(name)?.forEach(child => fields.add(child)));
    }

    let changed = false;
    fields.forEach(name => {
      const inputState = this.inputState.get(name);
      if (!has(this.values, name) && !has(this.initialValues, name)
        && !has(this.validationValues, name) && this.newValues.get(name) === undefined
        && inputState?.value === undefined) {
        return;
      }
      if (!changed) {
        this.oldValues = { ...this.values };
        changed = true;
      }
      const previousValue = lodashGet(this.values, name) ?? lodashGet(this.initialValues, name);
      this.newValues.set(name, undefined);
      if (has(this.initialValues, name)) {
        this.initialValues = omitFieldPath(this.initialValues, name);
      }
      if (has(this.values, name)) {
        this.values = omitFieldPath(this.values, name);
      }
      if (has(this.validationValues, name)) {
        this.validationValues = omitFieldPath(this.validationValues, name);
      }
      inputState?.setValue(undefined);
      if (includeDependents) {
        this.errors.delete(name);
        const controlType = [...(this.schema ?? []), ...(this.workspaceState?.schema ?? [])]
          .find(item => item.name === name)?.type;
        this.workspaceState?.markFormInputCleared?.(name, previousValue, controlType);
      }
    });
  };

  @action public setApprovalMap = (approvals) => {
    set(this, 'approvalMap', { ...this.approvalMap, ...approvals });
  };

  @action public setRules = (rules: Dictionary<string>) =>
    set(this.rules, rules);

  @action public getRules = () => this.rules;

  @action public setErrors = (errors: Dictionary<string>) =>
    set(this, 'errors', errors);

  @action public setFieldError = (fieldName: string, error: string) =>
    set(this.errors, fieldName, error);

  @action public getFieldError = (fieldName: string): string | undefined =>
    get(this.errors, fieldName);

  @action public setHelpers = (helpers: Dictionary<string>) => {
    set(this, 'helpers', helpers);
  };

  @action public setFieldHelper = (fieldName: string, value: string) => {
    set(this.helpers, fieldName, value);
  };

  @action public getFieldHelper = (fieldName: string) =>
    get(this.helpers, fieldName);

  @action public setFieldRules = (fieldName: string, rules: string | undefined) => {
    if (isUndefined(rules)) {
      return;
    }
    this.rules = {
      ...this.rules,
      [fieldName]: rules,
    };
  };

  @action public setFieldFinalValidationRules = (fieldName: string, rules: string | undefined) => {
    if (isUndefined(rules)) {
      return;
    }
    this.finalValidationRulse = {
      ...this.finalValidationRulse,
      [fieldName]: rules,
    };
  };

  @action public setValidationAttributeName = (fieldName: string, attrName: string) => {
    if (this.validationAttributeNames[fieldName] === attrName) { return; }
    this.validationAttributeNames = {
      ...this.validationAttributeNames,
      [fieldName]: attrName,
    };
  };

  @action public removeFieldRules = (fieldName: string) => {
    const rules = { ...this.rules };
    const finalValidationRulse = { ...this.finalValidationRulse };
    if (isUndefined(rules[fieldName]) || isUndefined(finalValidationRulse[fieldName])) {
      return;
    }
    delete rules[fieldName];
    delete finalValidationRulse[fieldName];
    this.rules = {
      ...rules,
    };
    this.finalValidationRulse = {
      ...finalValidationRulse,
    };
  };

  /** Validate once after the current batch of input registration effects. */
  public requestMountValidation = (inputOwner = true): (() => void) => {
    const owner = Symbol('form-validation-owner');
    if (inputOwner) {
      this.validationOwners.add(owner);
    }
    const batch = this.pendingMountValidation ?? new Set<symbol>();
    if (!this.pendingMountValidation) {
      this.pendingMountValidation = batch;
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      Promise.resolve().then(() => {
        if (this.pendingMountValidation !== batch) { return; }
        this.pendingMountValidation = undefined;
        if (batch.size > 0) {
          this.validate();
        }
      });
    }
    batch.add(owner);

    return () => {
      batch.delete(owner);
      if (!inputOwner || !this.validationOwners.delete(owner)) { return; }
      if (this.validationOwners.size === 0) {
        this.flushChangeValidation();
        this.debouncedChangeValidation.cancel();
        this.pendingMountValidation = undefined;
      }
    };
  };

  public scheduleChangeValidation = (): void => {
    // An older asynchronous result must not repaint errors while newer input is queued.
    this.validationSequence++;
    this.changeValidationPending = true;
    this.debouncedChangeValidation();
  };

  public flushChangeValidation = (): void => {
    if (this.changeValidationPending) {
      this.validate();
    }
  };

  @action private applyValidationErrors = (errors: Dictionary<string[]>): void => {
    this.errors.forEach((_message, fieldName) => {
      if (!Object.prototype.hasOwnProperty.call(errors, fieldName)) {
        this.errors.delete(fieldName);
      }
    });
    map(errors, (messages, fieldName) => {
      const message = join(messages, '\n');
      if (this.errors.get(fieldName) !== message) {
        this.errors.set(fieldName, message);
      }
    });
  };

  @action public validate = (callback?: (valid: boolean) => any, final = false) => {
    // Explicit validation supersedes both typing and input-mount requests.
    this.debouncedChangeValidation.cancel();
    this.changeValidationPending = false;
    this.pendingMountValidation = undefined;
    const validationSequence = ++this.validationSequence;
    if (isUndefined(this.Validator)) {
      return;
    }

    let rules = final ? this.finalValidationRulse : this.getRules();
    let values = omitBy(this.validationValues, (v) => (isString(v) && isEmpty(v)) || (isArray(v) && isEmpty(v)));

    // *TODO: unify validators format
    let validator: any;
    try {
      validator = new this.Validator(values, rules);
    } catch {
      rules = flatten(this.getRules());
      values = flatten(this.getValues());
    }
    if (!validator) {
      return;
    }
    const { document: documentRevision } = this.workspaceState ?? {};
    this.validatorRules = validator.rules;
    validator = new this.Validator(values, this.parseRules(rules, values));
    this.registerValidator(validator);
    validator.setAttributeNames(this.validationAttributeNames);
    validator.context = {
      documentId: documentRevision?.id ?? null,
    };
    validator.checkAsync(
      // passes
      () => {
        if (validationSequence === this.validationSequence) {
          this.applyValidationErrors({});
        }
        callback && callback(true);
      },
      () => { // failed
        if (validationSequence === this.validationSequence) {
          this.applyValidationErrors(validator.errors.all());
        }
        callback && callback(false);
      });
  };

  private readonly parseRules = (rules: Dictionary<string>, values: Dictionary<string>): Dictionary<string> => {
    const parsedRules = {};
    map(rules, (value, key) => {
      if (!isString(value)) { return; }
      const rulesArray = value?.split('|');
      const valuesArray: any = [];
      map(rulesArray, (val) => {
        if (!val) {
          return;
        }
        const splitVal = val.split(':');
        if (splitVal.length === 1) {
          parsedRules[key] = val;
        } else {
          const finalValue = splitVal[1].split(',');
          if (finalValue.length === 1) {
            valuesArray.push({
              [splitVal[0]]: finalValue[0],
            });
          } else if (splitVal.length === 2) {
            let fromValue: any = values[finalValue[0]];
            fromValue = isNumber(fromValue) ? toNumber(finalValue[1]) : finalValue[1];
            valuesArray.push({
              [splitVal[0]]: [finalValue[0], fromValue],
            });
          } else { // array value (required_if_in validator)
            // const ruleName = splitVal[1].split(",")[0];
            const splitArray = splitVal[1].split(',');
            const splitValue = val.split(',');
            const finalValue = slice(splitValue, 1, splitValue.length).join(',');
            valuesArray.push({
              [splitVal[0]]: [splitArray[0], finalValue],
            });
          }
          parsedRules[key] = valuesArray;
        }
      });
    });
    return parsedRules;
  };

  // *NOTE: remove this after it is set in be script
  public registerValidator = (validator: any) => {
    this.Validator.registerAsyncImplicit(
      'required_if_in',
      function (val: string[], req: string[], attribute: string, passes) {
        const reqs = req?.[1];
        const reqName = req?.[0] || '';
        let inputValue = lodashGet(validator.input, reqName);
        // Radio group support
        !isArray(inputValue) && (inputValue = [inputValue]);
        const diff = inputValue && inputValue.filter((inputVal) => {
          try {
            const ruleParams = values(JSON.parse(reqs));
            if (inputVal && typeof inputVal === 'object') {
              return ruleParams.some((convertedItem) => isEqual(inputVal, convertedItem));
            }

            return ruleParams.includes(inputVal);
          } catch {
            return reqs.includes(inputVal);
          }
        });
        if (diff && diff.length > 0 && (!val || isEmptyValue(val))) {
          const { messages: { attributeNames } } = validator;
          // validator message changed according to ENC-5130
          // `The ${attributeNames[attribute]} field is required when ` +
          //  `${attributeNames[reqName]} includes ${reqsAttributes.join(", ")}.`
          // const reqsAttributes = reqs.map((req) => attributeNames[req]);
          return passes(
            false,
            `The ${attributeNames[attribute]} is a required field.`);
        }
        return passes();
      });
    this.Validator.registerAsyncImplicit(
      'required_if',
      function (val: string[], req: string[], attribute: string, passes) {
        const reqs = req?.[1];
        const reqName = req?.[0] || '';
        const inputValue = lodashGet(validator.input, reqName);
        const { messages: { attributeNames } } = validator;
        if (!inputValue) { return passes(); }
        if (!val && reqs === inputValue) {
          return passes(
            false,
          `The ${attributeNames[attribute]} field is required when ${attributeNames[reqName]} is ${attributeNames[reqs] ?? reqs}`);
        }

        return passes();
      });
    this.Validator.registerAsyncImplicit(
      'required_with',
      function (val: any, req: string[] | string, attribute: string, passes) {
        const fields = Array.isArray(req) ? req : typeof req === 'string' ? req.split(',') : [];

        const {
          messages: { attributeNames },
        } = validator;

        let shouldRequire = false;

        for (let i = 0; i < fields.length; i++) {
          const fieldName = fields[i];
          const inputValue = lodashGet(validator.input, fieldName);

          if (inputValue !== undefined && inputValue !== null && inputValue !== '') {
            shouldRequire = true;
            break;
          }
        }

        if (shouldRequire && (!val || isEmptyValue(val))) {
          return passes(
            false,
            translate('validator.required.with.field.error.message', {
              fieldName: attributeNames[attribute] || attribute,
              fields: fields.map((f) => attributeNames[f] || f).join(', '),
            }),
          );
        }

        return passes();
      },
    );

    this.Validator.registerAsyncImplicit(
      'required_without',
      function (val: any, req: string[] | string, attribute: string, passes) {
        const fields = Array.isArray(req) ? req : typeof req === 'string' ? req.split(',') : [];

        const {
          messages: { attributeNames },
        } = validator;

        let shouldRequire = false;

        for (let i = 0; i < fields.length; i++) {
          const fieldName = fields[i];
          const inputValue = lodashGet(validator.input, fieldName);

          if (inputValue === undefined || inputValue === null || inputValue === '') {
            shouldRequire = true;
            break;
          }
        }

        if (shouldRequire && (!val || isEmpty(val))) {
          return passes(
            false,
            translate('validator.required.with.out.field.error.message', {
              fieldName: attributeNames[attribute] || attribute,
              fields: fields.map((f) => attributeNames[f] || f).join(', '),
            }),
          );
        }

        return passes();
      },
    );
  };

  public setFieldAutosave = (name?: string) => {
    this.flushChangeValidation();
    if (!name) { return; }
    const inputState = this.inputState.get(name);
    if (!inputState) { return; }
    const formValue = cloneDeep(this.getFieldValue(name));
    inputState.setAutosaveValue(formValue);
  };

  public lockField = (
    body: Partial<FBLockBody>,
    fieldName: string,
  ) => {
    // if (!this.includeLocking) { return; }
    // if (this.locked?.get(fieldName)?.locked) { return; }
    // set(this, "isBackdropOpen", true);
    // this.locked.set(fieldName, {
    //   ...this.locked.get(fieldName),
    //   fieldName,
    // });
    // SMStore.post<FBLockData, Partial<FBLockBody>>({
    //   url: SMUrl.lockFields,
    //   body,
    // }, (data, error) => {
    //   if (error) {
    //     const { statusCode } = error || {};
    //     if (!statusCode || statusCode === 500) { return; }
    //   }
    //   const {
    //     lock: { id = undefined } = {},
    //     documentRevision: { owner: { user: { email: ownerName = undefined } = {} } = {} } = {},
    //   } = data || {};
    //   if (!id) {
    //     set(this, "isBackdropOpen", false);
    //     return;
    //   }
    //   this.locked.set(fieldName, {
    //     ...this.locked.get(fieldName),
    //     locked: true,
    //     fieldName,
    //     lockId: id,
    //     ownerName: ownerName || this.workspaceState?.currentUser?.email,
    //   });
    //   set(this, "isBackdropOpen", false);
    // });
  };

  public unlockField = (fieldName?: string) => {
    // if (!this.includeLocking) { return; }
    // if (!fieldName) { return; }
    // const lockedData = this.locked.get(fieldName);
    // const { lockId: id } = lockedData || {};
    // if (!id || isEmpty(id)) { return; }

    // SMStore.post({
    //   url: SMStore.templateUrl(SMUrl.lockFieldsExpire, { id }),
    // }, (_data, error) => {
    //   if (error) {
    //     return;
    //   }
    //   this.locked.set(fieldName, {
    //     ...lockedData,
    //     locked: false,
    //     lockId: undefined,
    //   });
    // });
  };

  public lockFields = (fields: string[]): void => {
    if (!this.includeLocking) { return; }
    if (!fields.length) { return; }
    fields.forEach((field) => {
      this.locked.set(field, {
        ...this.locked.get(field),
        locked: true,
      });
    });
  };

  public unlockFields = (fields: string[]): void => {
    if (!this.includeLocking) { return; }
    if (!fields.length) { return; }
    fields.forEach((field) => {
      this.locked.set(field, {
        ...this.locked.get(field),
        locked: false,
      });
    });
  };
}

export default FBFormState;
